<template>
  <main class="user-center">
    <div v-if="showCheckinSuccess" class="user-center__overlay"></div>
    <div v-if="showCheckinSuccess" class="user-center__toast" role="status">
      <button type="button" aria-label="关闭签到提示" @click="closeCheckinSuccess">
        <AppIcon name="close" size="sm" aria-label="" />
      </button>
      今日签到成功，奖励您 2 金币
    </div>

    <div class="user-center__container">
      <header class="user-center__profile">
        <button
          type="button"
          class="user-center__profile-main"
          @click="goToModule('UserBaseInfo')"
        >
          <img :src="user.avatar" :alt="`${user.name}的头像`" />
          <span class="user-center__identity">
            <strong>{{ user.name }}</strong>
            <small>欢迎回到橘子漫嘎</small>
          </span>
        </button>
      </header>

      <template v-if="isMobile">
        <section class="user-center__wallet user-center__wallet--mobile">
          <div class="user-center__section-heading">
            <span><AppIcon name="wallet" size="md" />我的钱包</span>
          </div>
          <div class="user-center__stats">
            <button type="button" aria-label="打开橘子币充值" @click="openGoldBalance">
              <small>当前余额</small>
              <span class="user-center__stat-value">
                <AppIcon name="orangeCoin" size="md" aria-hidden="true" />
                <strong>{{ goldAmount }}</strong>
              </span>
            </button>
            <button type="button" @click="goToModule('PurchaseHistory', { recordType: 'CONSUME' })">
              <small>累计消费</small>
              <span class="user-center__stat-value">
                <AppIcon name="totalSpend" size="md" aria-hidden="true" />
                <strong>{{ totalConsumeAmount }}</strong>
              </span>
            </button>
          </div>
        </section>

        <section class="user-center__mobile-actions">
          <h2>
            <AppIcon name="home" size="md" />
            我的空间
          </h2>
          <div>
            <button
              v-for="action in quickActions"
              :key="action.name"
              type="button"
              @click="goToModule(action.name)"
            >
              <span><AppIcon :name="customIcons[action.icon]" size="xl" /></span>
              {{ action.label }}
            </button>
          </div>
        </section>

        <button type="button" class="user-center__checkin" @click="showCheckinCalendar">
          <span class="user-center__checkin-body">
            <span class="user-center__checkin-title">
              <AppIcon name="calendarCheck" size="lg" aria-hidden="true" />
              <strong>每日签到</strong>
            </span>
            <small>{{ checkinDescription }}</small>
          </span>
          <AppIcon name="chevronRight" size="sm" />
        </button>
      </template>

      <div v-else class="user-center__content">
        <aside class="user-center__sidebar">
          <section class="user-center__wallet">
            <div class="user-center__section-heading">
              <span><AppIcon name="wallet" size="md" />我的钱包</span>
            </div>
            <div class="user-center__stats">
              <button type="button" aria-label="打开橘子币充值" @click="openGoldBalance">
                <small>当前余额</small>
                <span class="user-center__stat-value">
                  <AppIcon name="orangeCoin" size="md" aria-hidden="true" />
                  <strong>{{ goldAmount }}</strong>
                </span>
              </button>
              <button type="button" @click="goToModule('PurchaseHistory', { recordType: 'CONSUME' })">
                <small>累计消费</small>
                <span class="user-center__stat-value">
                  <AppIcon name="totalSpend" size="md" aria-hidden="true" />
                  <strong>{{ totalConsumeAmount }}</strong>
                </span>
              </button>
            </div>
            <button type="button" class="user-center__checkin" @click="showCheckinCalendar">
              <span class="user-center__checkin-body">
                <span class="user-center__checkin-title">
                  <AppIcon name="calendarCheck" size="lg" aria-hidden="true" />
                  <strong>每日签到</strong>
                </span>
                <small>{{ checkinDescription }}</small>
              </span>
              <AppIcon name="chevronRight" size="sm" />
            </button>
          </section>

          <nav class="user-center__nav" aria-label="个人中心功能">
            <p>
              <AppIcon name="home" size="md" />
              我的空间
            </p>
            <button
              v-for="item in accountItems"
              :key="item.name"
              type="button"
              :class="{ 'is-active': activeSubMenu === item.name }"
              @click="navigateTo(item.name)"
            >
              <AppIcon
                :name="customIcons[item.icon]"
                size="md"
                :variant="activeSubMenu === item.name ? 'active' : 'default'"
              />
              <span>{{ item.label }}</span>
              <AppIcon
                name="chevronRight"
                size="sm"
                class="user-center__nav-arrow"
              />
            </button>
          </nav>
        </aside>

        <section class="user-center__operation">
          <component :is="activeComponent" v-if="activeComponent" />
        </section>
      </div>
    </div>

    <CheckinCalendar
      :show-calendar="showCalendar"
      :continuous-days="user.continuousDays || 0"
      @close="closeCheckinCalendar"
      @checkin-success="handleCheckinSuccess"
    />
  </main>
</template>

<script setup>
import { computed } from 'vue'
import AppIcon from '@/shared/components/AppIcon.vue'
import CheckinCalendar from '@/features/user/components/CheckinCalendar.vue'
import { useUserCenterPage } from '@/features/user/composables/useUserCenterPage'

const customIcons = {
  Wallet: 'wallet',
  Medal: 'crown',
  User: 'profile',
  Star: 'heart',
  Clock: 'history',
  ChatDotRound: 'feedback',
  Key: 'lock',
}

const {
  user,
  isMobile,
  activeComponent,
  activeSubMenu,
  showCheckinSuccess,
  showCalendar,
  accountItems,
  quickActions,
  goldAmount,
  totalConsumeAmount,
  navigateTo,
  goToModule,
  openGoldBalance,
  closeCheckinSuccess,
  showCheckinCalendar,
  closeCheckinCalendar,
  handleCheckinSuccess,
} = useUserCenterPage()

const checkinDescription = computed(() =>
  user.value.continuousDays > 0
    ? `已连续签到 ${user.value.continuousDays} 天`
    : '签到领橘子币'
)
</script>

<style scoped src="@/features/user/styles/UserCenter.css"></style>
<style scoped src="@/features/user/styles/UserCenterMobile.css"></style>
